import React, { useRef } from "react";
import { Input, InputGroup, InputNumber, SelectPicker } from "rsuite";
import { Col, FormGroup, Label, Row } from "reactstrap";
import PropTypes from "prop-types";
import {
  discountTypes,
  DAYS,
  dayMap,
  MAX_PERCENT_DISCOUNT,
  MAX_FIXED_DAY_DISCOUNT,
} from "./offerTypeConstants";

/**
 * DaysAmountInput - Second row of OfferType form
 * Contains Days applied on selector and Amount/Day-wise inputs
 */
function DaysAmountInput({
  discountType,
  onDiscountTypeChange,
  mCompDiscountType,
  discountValue,
  onDiscountChange,
  percentDiscountMap = {},
  fixedDiscountMap = {},
  onDayChange,
  onKeyDown,
  errorMessage = null,
}) {
  const daysAppliedOnContainerRef = useRef(null);

  // Determine if percentage type (value 2 = percentage)
  const isPercentageType = mCompDiscountType === 2;

  // A percentage is a share of the rate, so it stops at 100 (100 itself is a
  // valid "free night"); flat amounts keep their wider currency range. This
  // only bounds the native stepper — a typed or pasted 150 is kept, flagged by
  // errorMessage and blocked at the footer. The all-days input is deliberately
  // left unbounded here: rsuite's InputNumber silently rewrites an
  // out-of-range entry to the max on blur, which would turn the 150 the user
  // typed into an accepted 100 behind their back.
  const maxDayValue = isPercentageType
    ? MAX_PERCENT_DISCOUNT
    : MAX_FIXED_DAY_DISCOUNT;

  const renderDayInput = (day) => {
    const currentValue = isPercentageType
      ? percentDiscountMap[dayMap.get(day)]
      : fixedDiscountMap[dayMap.get(day)];

    return (
      <FormGroup key={day}>
        <Label for={`day-${day}`}>{day}</Label>
        <InputGroup className="day-input-container" inside>
          <Input
            id={`day-${day}`}
            className="day-input"
            type={"number"}
            step={0.01}
            min="0"
            max={maxDayValue}
            onClick={(e) => e.target.select()}
            onChange={(val) => onDayChange(val, day)}
            onKeyDown={(e) => onKeyDown(e, day)}
            value={currentValue || "0"}
          />
          <InputGroup.Addon>{isPercentageType ? "%" : ""}</InputGroup.Addon>
        </InputGroup>
      </FormGroup>
    );
  };

  return (
    <Row className="mt-3">
      {/* Second Row: Days applied on and Amount */}
      <Col md={4}>
        <FormGroup>
          <Label for="daysAppliedOn">Days applied on</Label>
          <div className="position-relative" ref={daysAppliedOnContainerRef}>
            <SelectPicker
              id="daysAppliedOn"
              value={discountType}
              onChange={(value) => onDiscountTypeChange(value)}
              placeholder="Choose days"
              data={discountTypes}
              cleanable={false}
              searchable={false}
              block
              container={() => daysAppliedOnContainerRef.current}
            />
          </div>
        </FormGroup>
      </Col>
      <Col md={8}>
        <FormGroup>
          {discountType !== 1 && <Label for="amount">Amount</Label>}
          {discountType === 1 ? (
            <div className="day-inputs-container">
              <div className="day-inputs-grid">
                {DAYS.map((day) => renderDayInput(day))}
              </div>
            </div>
          ) : (
            <InputGroup className="amount-input-container" inside>
              <InputNumber
                id="amount"
                name="amount"
                value={discountValue}
                onChange={(value) => onDiscountChange(value)}
                placeholder=""
              />
              {isPercentageType && (
                <InputGroup.Addon className="day-input-addon">
                  %
                </InputGroup.Addon>
              )}
            </InputGroup>
          )}
          {errorMessage && (
            <p className="discount-limit-error" role="alert">
              {errorMessage}
            </p>
          )}
        </FormGroup>
      </Col>
    </Row>
  );
}

DaysAmountInput.propTypes = {
  discountType: PropTypes.number,
  onDiscountTypeChange: PropTypes.func.isRequired,
  mCompDiscountType: PropTypes.number,
  discountValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDiscountChange: PropTypes.func.isRequired,
  percentDiscountMap: PropTypes.object,
  fixedDiscountMap: PropTypes.object,
  onDayChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
};

export default DaysAmountInput;
